import type { SavedPicture } from './types.js'
import type {
  ModelLabelFindings,
  CountertopFindings,
  UndersinkFindings,
} from '../state/store.js'

export interface DemoScanContent<F> {
  /** Public-path URL of the H.264 mp4 — universally supported fallback. */
  videoUrlMp4: string
  /**
   * Public-path URL of the VP9 webm — smaller bytes; Chrome and Firefox
   * pick this first, Safari falls back to mp4.
   */
  videoUrlWebm: string
  /**
   * Public-path URL of the .jpg shown by the <video> element while the
   * recording loads (i.e. the `poster` attribute). First frame of the
   * scan's segment, shipped alongside the cut. NOT the captured photo
   * the demo claims to take — that lives in `picture.dataUrl`.
   */
  posterUrl: string
  /**
   * Seed picture pushed into `savedPictures` when the video ends. This is
   * the actual photo the demo claims to "capture" (different from the
   * poster). `scan` and `takenAt` are stamped at dispatch time.
   */
  picture: Omit<SavedPicture, 'scan' | 'takenAt'>
  /** Findings the scan would have reported via `advance_step`. */
  findings: F
}

export const DEMO_CONTENT: {
  'model-label': DemoScanContent<ModelLabelFindings>
  countertop: DemoScanContent<CountertopFindings>
  undersink: DemoScanContent<UndersinkFindings>
} = {
  'model-label': {
    videoUrlMp4: '/demo/model-label.mp4',
    videoUrlWebm: '/demo/model-label.webm',
    posterUrl: '/demo/model-label.jpg',
    picture: {
      label: 'Model & serial label',
      dataUrl: '/demo/captured/model-label.jpg',
    },
    findings: {
      brand: 'Whirlpool',
      model: 'WDT720PADM2',
      rawLabelText: 'WHIRLPOOL  MOD WDT720PADM2  SER F61400039  TYPE 583-1',
    },
  },
  countertop: {
    videoUrlMp4: '/demo/countertop.mp4',
    videoUrlWebm: '/demo/countertop.webm',
    posterUrl: '/demo/countertop.jpg',
    picture: {
      label: 'Countertop edge',
      dataUrl: '/demo/captured/countertop.jpg',
    },
    findings: {
      material: 'Quartz',
      overhangInches: 0.5,
      materialConfidence: 0.92,
    },
  },
  undersink: {
    videoUrlMp4: '/demo/undersink.mp4',
    videoUrlWebm: '/demo/undersink.webm',
    posterUrl: '/demo/undersink.jpg',
    picture: {
      label: 'Under-sink plumbing',
      dataUrl: '/demo/captured/undersink.jpg',
    },
    findings: {
      valve: 'quarter-turn',
      drain: 'standard-hose',
      outlet: { present: false, side: 'none-visible' },
      drainLoopInches: 30,
    },
  },
}
